import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";

const NOTIFICATION_COUNT_URL = `${API_BASE_URL}notifications/counts`;
const REFRESH_URL = `${API_BASE_URL}auth/trader/jwt/refresh/`;

const NotificationCountContext = createContext(null);

const hasStoredAuth = () =>
  Boolean(localStorage.getItem("access") || localStorage.getItem("refresh"));

const getCountValue = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};

const NotificationCountProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const wasAuthenticatedRef = useRef(false);
  const didCheckInitialAuthRef = useRef(false);
  const [notifCount, setNotifCount] = useState(0);
  const [ticketCount, setTicketCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetCounts = useCallback(() => {
    setNotifCount(0);
    setTicketCount(0);
    setError(null);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    resetCounts();
    navigate("/signin");
  }, [navigate, resetCounts]);

  const refreshAccessToken = useCallback(async () => {
    const refreshResponse = await fetch(REFRESH_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logout();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  }, [logout]);

  const fetchNotificationCounts = useCallback(async () => {
    if (!hasStoredAuth()) {
      resetCounts();
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      let response = await fetch(NOTIFICATION_COUNT_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });

      if (response.status === 401) {
        const nextAccessToken = await refreshAccessToken();
        if (!nextAccessToken) {
          return null;
        }

        response = await fetch(NOTIFICATION_COUNT_URL, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        });
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      setNotifCount(getCountValue(data.unread_notif_count));
      setTicketCount(getCountValue(data.unreach_ticket_count));
      return data;
    } catch (fetchError) {
      console.error("Error fetching notification counts:", fetchError);
      setError(fetchError);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [refreshAccessToken, resetCounts]);

  useEffect(() => {
    const authenticated = hasStoredAuth();
    const shouldFetch =
      authenticated &&
      (!didCheckInitialAuthRef.current || !wasAuthenticatedRef.current);

    didCheckInitialAuthRef.current = true;
    wasAuthenticatedRef.current = authenticated;

    if (!authenticated) {
      resetCounts();
      return;
    }

    if (shouldFetch) {
      fetchNotificationCounts();
    }
  }, [fetchNotificationCounts, location.key, resetCounts]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (!["access", "refresh"].includes(event.key)) {
        return;
      }

      if (hasStoredAuth()) {
        wasAuthenticatedRef.current = true;
        fetchNotificationCounts();
        return;
      }

      wasAuthenticatedRef.current = false;
      resetCounts();
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [fetchNotificationCounts, resetCounts]);

  const value = useMemo(
    () => ({
      notifCount,
      ticketCount,
      setNotifCount,
      setTicketCount,
      isLoading,
      error,
      refetchNotificationCounts: fetchNotificationCounts,
    }),
    [notifCount, ticketCount, isLoading, error, fetchNotificationCounts],
  );

  return (
    <NotificationCountContext.Provider value={value}>
      {children}
    </NotificationCountContext.Provider>
  );
};

const useNotificationCounts = () => {
  const context = useContext(NotificationCountContext);
  if (!context) {
    throw new Error(
      "useNotificationCounts must be used within NotificationCountProvider",
    );
  }
  return context;
};

export { NotificationCountProvider, useNotificationCounts };
